import { Button, Box, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../../../config/firebase.config";

import { useAuth } from "../../../contexts/AuthContext";
import { GoogleSignInRequestUserTypeEnum } from "../../../libs/cellera-sdk";
import { AuthResult } from "../../../types";

interface GoogleSignInProps {
  userType?: GoogleSignInRequestUserTypeEnum;
  onSuccess?: (result: AuthResult) => void;
  onError?: (error: Error) => void;
  rememberMe: boolean;
  fullWidth?: boolean;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({
  userType = GoogleSignInRequestUserTypeEnum.Admin,
  onSuccess,
  onError,
  rememberMe,
  fullWidth = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithGoogle } = useAuth();

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithGoogle(userType, rememberMe);

      // Handle the case where result might be undefined (redirect flow)
      if (result) {
        if (onSuccess) onSuccess(result);
      }
      // If result is undefined, it means redirect flow is in progress
      // and the callback will be handled by the redirect handler
    } catch (error) {
      // If backend API failed, sign out the user to prevent partial authentication state
      // This handles cases like CROSS_PLATFORM_LOGIN_NOT_ALLOWED or ADMIN_USER_NOT_FOUND
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Backend rejected the sign-in, sign out to prevent redirect loops
        await signOut(auth);
        console.warn("Backend API call failed, signing out user:", error);
      }
      if (onError) onError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Button
        variant="outlined"
        className="rounded-full py-3 capitalize text-gray-900"
        fullWidth={fullWidth}
        onClick={handleGoogleSignIn}
        disabled={isLoading}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <div className="flex gap-2 items-center justify-center">
            <img src="/google.svg" alt="Google" />
            <span>Log In With Google</span>
          </div>
        )}
      </Button>
    </Box>
  );
};

export default GoogleSignIn;
